/* THE SESSION TRACE — a black-box recorder for a play session.
 *
 * Capture every meaningful FEATURE ACTIVATION (not the heartbeats, pings and
 * 4-5s polls — those are noise that would bury the signal) with WHO triggered it
 * and WHETHER it worked, persist it through reloads, and export it as one JSON
 * file the DM hands to Claude to analyse after a session.
 *
 * It is a sibling of the health board (state/health.ts): the board collapses the
 * same outcome stream to four live lamps and throws it away; this keeps it at full
 * per-action resolution and persists it. Fed at ONE point — bridge.ts request() —
 * for increment 1 (every outbound Roll20 / D&D Beyond / Compass write, scan and
 * import, which is ~all DM-initiated feature traffic). Inbound player events and
 * true player names come later (they live on the Compass net layer).
 *
 * The honest column is `outcome`: a lane that answered `ok` is "trusted" unless we
 * actually confirmed it server-side ("verified") — the P0-3 lesson, where ok:true
 * did not mean the write landed. `failed` and `uncertain` are self-evident.
 *
 * Deliberately never throws into a request, and never blocks on storage.
 */
import { systemOf, type System } from "./health";

export type TraceOutcome = "verified" | "trusted" | "failed" | "uncertain";
export type TraceActor = "dm" | "system" | `player:${string}`;

export interface TraceEntry {
  at: string;              // ISO timestamp
  actor: TraceActor;       // who triggered it
  surface: System;         // connector | roll20 | ddb | compass
  feature: string;         // human label (e.g. "Roll20 HP/bar write")
  lane: string;            // technical lane id
  outcome: TraceOutcome;   // verified | trusted | failed | uncertain
  durationMs?: number;
  detail?: string;         // compact summary of what it touched
  error?: string | null;
  correlationId?: string;
}

const CAP = 3000;                           // a long night is a few hundred; 3000 is headroom
const KEY = "dmscreen.sessionTrace.v1";
let entries: TraceEntry[] = restore();
const subs = new Set<() => void>();

function restore(): TraceEntry[] {
  try { const raw = typeof localStorage !== "undefined" ? localStorage.getItem(KEY) : null; if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) return v.slice(-CAP); } } catch { /* blocked or corrupt — start empty */ }
  return [];
}
let flushTimer = 0;
function flush() { try { localStorage.setItem(KEY, JSON.stringify(entries)); } catch { /* storage full/blocked — the in-memory copy still exports */ } }
function scheduleFlush() { if (flushTimer || typeof window === "undefined") return; flushTimer = window.setTimeout(() => { flushTimer = 0; flush(); }, 2000); }
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flush);
  window.addEventListener("visibilitychange", () => { if (typeof document !== "undefined" && document.visibilityState === "hidden") flush(); });
}

function pushTrace(e: TraceEntry) {
  entries.push(e);
  if (entries.length > CAP) entries = entries.slice(-CAP);
  subs.forEach((fn) => fn());
  scheduleFlush();
}

/* lane -> human label; most-specific first, with a prettified fallback. */
const LABELS: [RegExp, string][] = [
  [/ROLL20_BARS/, "Roll20 HP/bar write"],
  [/ROLL20_TOKENS/, "Roll20 token write"],
  [/ROLL20_TURN/, "Roll20 turn order"],
  [/ROLL20_(ANNOUNCE|CHAT)/, "Roll20 chat"],
  [/ROLL20_(AURA|EFFECT|STATUS)/, "Roll20 aura/marker"],
  [/ROLL20/, "Roll20"],
  [/(DDB_LIVE|HEALTH|DDB_STATUS|STATUS_WRITE)/, "D&D Beyond HP/status write"],
  [/SLOT/, "D&D Beyond spell slot"],
  [/(DM_NOTES|TAGGED_NOTE)/, "D&D Beyond notes"],
  [/(INVENTORY|COIN|EQUIP|ITEM)/, "D&D Beyond inventory"],
  [/MAGIC_ITEM/, "D&D Beyond magic item"],
  [/(SCAN|IMPORT|CATALOG|SHEET|SPELLBOOK)/, "Import / scan"],
  [/LMC_EFFECTS/, "Compass effects"],
  [/(LMC_ROLL|COMPASS_ROLL|LMC_DICE)/, "Compass dice"],
  [/(LMC_PREFS|PREFS)/, "Compass prefs"],
  [/LMC_TOKENS/, "Compass token sync"],
  [/LMC/, "Compass"],
];
function labelOf(lane: string): string {
  for (const [re, label] of LABELS) if (re.test(lane)) return label;
  return lane.replace(/^DM_SCREEN_/, "").replace(/_(REQUEST|RESULT)$/, "").replace(/_/g, " ").toLowerCase();
}

/* Lanes that confirm a write against server-authoritative state (re-read / await
 * a confirmation). Anything NOT here is reported "trusted" — the honest default,
 * meaning "the lane said ok and we did not independently verify it landed". Grown
 * as more lanes gain verification (the Phase A reliability track). */
const VERIFIED: RegExp[] = [
  /DM_NOTES_EXPORT/, /INVENTORY_EXPORT/, /INVENTORY_MUTATION/, /MAGIC_ITEM/,
  /DDB_STATUS/, /DDB_HEALTH/, /STATUS_WRITE/, /HEALTH_WRITE/, /SLOT/, /EXPORT_PREFLIGHT/,
];
function confidence(lane: string, verifiedFlag?: boolean): "verified" | "trusted" {
  if (verifiedFlag) return "verified";
  return VERIFIED.some((re) => re.test(lane)) ? "verified" : "trusted";
}

function summarize(message: Record<string, unknown>): string | undefined {
  const bits: string[] = [];
  const name = (message as Record<string, unknown>).name;
  if (typeof name === "string" && name) bits.push(name);
  for (const k of ["updates", "targets", "sourceUrls"] as const) {
    const v = (message as Record<string, unknown>)[k];
    if (Array.isArray(v)) bits.push(`${v.length} ${k === "updates" ? "update" : k === "targets" ? "target" : "source"}${v.length === 1 ? "" : "s"}`);
  }
  return bits.length ? bits.slice(0, 3).join(" · ") : undefined;
}

/** The one capture call bridge.ts makes at each request resolution/timeout. */
export function traceRequest(lane: string, message: Record<string, unknown>, r: { ok: boolean; uncertain?: boolean; error?: string; startedAt: number; verified?: boolean }) {
  try {
    const outcome: TraceOutcome = r.uncertain ? "uncertain" : !r.ok ? "failed" : confidence(lane, r.verified);
    const cid = typeof message.requestId === "string" ? message.requestId
      : typeof message.scanId === "string" ? message.scanId
      : typeof message.exportId === "string" ? message.exportId : undefined;
    pushTrace({
      at: new Date().toISOString(), actor: "dm", surface: systemOf(lane),
      feature: labelOf(lane), lane, outcome,
      durationMs: Math.max(0, Date.now() - r.startedAt),
      detail: summarize(message), error: r.error || null, correlationId: cid,
    });
  } catch { /* the trace must never break a request */ }
}

export function traceEntries(): TraceEntry[] { return entries; }
export function traceCount(): number { return entries.length; }
export function subscribeTrace(fn: () => void): () => void { subs.add(fn); return () => subs.delete(fn); }
export function clearTrace() { entries = []; flush(); subs.forEach((fn) => fn()); }

/** Build the shareable document and trigger a download. */
export function exportTrace(meta: { campaign?: string; build?: string; connector?: string }): void {
  const doc = {
    session: { ...meta, start: entries[0]?.at, end: entries[entries.length - 1]?.at, exportedAt: new Date().toISOString(), count: entries.length },
    entries,
  };
  try {
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `session-trace-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { /* download blocked — nothing we can do, the data is still in localStorage */ }
}
